import React, { ReactNode, useCallback, useEffect, useState } from "react";

import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { bem } from "@react-md/utils";
// eslint-disable-next-line no-restricted-imports
import { PopconfirmProps as AntdPopconfirmProps } from "antd/es/popconfirm";
import Popover from "antd/es/popover";
import cn from "classnames";

import { isDefined } from "@mapmycustomers/shared/util/assert";
import { isNodeEmpty } from "@mapmycustomers/shared/util/react";

import Button, { ButtonProps } from "../Button";
import { useConfigProvider } from "../ConfigProvider";

const block = bem("mmc-popconfirm");

export interface PopconfirmProps extends Omit<
  AntdPopconfirmProps,
  "cancelButtonProps" | "okButtonProps" | "title"
> {
  cancelButtonProps?: Omit<ButtonProps, "danger" | "onClick" | "size" | "type">;
  children?: ReactNode;
  danger?: boolean;
  description: ReactNode;
  okButtonProps?: Omit<ButtonProps, "danger" | "onClick" | "size" | "type">;
  title?: ReactNode;
}

const Popconfirm = ({
  cancelButtonProps,
  cancelText,
  children,
  danger,
  description,
  disabled,
  icon,
  okButtonProps,
  okText,
  onCancel,
  onConfirm,
  onOpenChange,
  open,
  overlayClassName,
  title,
  trigger = ["click"],
  ...antDPopconfirmProps
}: PopconfirmProps) => {
  const configProvider = useConfigProvider();

  const isControlled = isDefined(open);
  const [internalOpen, setInternalOpen] = useState<boolean>(!!open);

  useEffect(() => {
    if (isControlled) {
      setInternalOpen(!!open);
    }
  }, [open, isControlled]);

  useEffect(() => {
    if (disabled) {
      setInternalOpen(false);
    }
  }, [disabled, internalOpen]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!isControlled) {
        setInternalOpen(nextOpen);
      }
      onOpenChange?.(nextOpen);
    },
    [isControlled, onOpenChange]
  );

  const handleClose = useCallback(() => {
    if (!isControlled) {
      setInternalOpen(false);
    }
    onOpenChange?.(false);
  }, [isControlled, onOpenChange]);

  const handleCancel = useCallback(
    (e?: React.MouseEvent<HTMLElement, MouseEvent>) => {
      onCancel?.(e);
      handleClose();
    },
    [handleClose, onCancel]
  );

  const handleConfirm = useCallback(
    (e?: React.MouseEvent<HTMLElement, MouseEvent>) => {
      onConfirm?.(e);
      handleClose();
    },
    [handleClose, onConfirm]
  );

  const renderIcon =
    icon === null ? null : (
      <div className={block("icon")}>
        {icon ?? <FontAwesomeIcon className={block("defaultIcon")} icon={faInfoCircle} />}
      </div>
    );

  const renderDescription = <div className={block("descriptionText")}>{description}</div>;

  const renderTitle = title ? (
    <div className={block("message")}>
      {title === true ? configProvider.formatMessage("ui.popconfirm.title") : title}
    </div>
  ) : (
    renderDescription
  );

  const footer = (
    <div className={block("footer")}>
      <Button {...cancelButtonProps} onClick={handleCancel} size="small">
        {cancelText ?? configProvider.formatMessage("ui.popconfirm.footer.cancelText")}
      </Button>
      <Button
        {...okButtonProps}
        danger={danger}
        onClick={handleConfirm}
        size="small"
        type="primary"
      >
        {okText ?? configProvider.formatMessage("ui.popconfirm.footer.okText")}
      </Button>
    </div>
  );

  const popoverContent = isNodeEmpty(description) ? null : (
    <div className={block("content")}>
      <div className={block("innerContent")}>
        <div className={block("title")}>
          {renderIcon}
          {renderTitle}
        </div>
        {title && (
          <div
            className={block("description", {
              hasIcon: icon !== null,
            })}
          >
            {renderDescription}
          </div>
        )}
      </div>
      {footer}
    </div>
  );

  return (
    <Popover
      content={popoverContent}
      onOpenChange={handleOpenChange}
      open={isControlled ? open : internalOpen}
      overlayClassName={cn(block({ danger }), overlayClassName)}
      trigger={disabled ? [] : trigger}
      {...antDPopconfirmProps}
    >
      {children}
    </Popover>
  );
};

export default Popconfirm;
